export type CapabilityAvailability = 'AVAILABLE' | 'READ_ONLY' | 'UNAVAILABLE'

export type CapabilityExecution = 'DIRECT' | 'APPROVAL_REQUIRED' | 'NONE'

export type CapabilityBacking = 'LIVE' | 'SURROGATE' | 'NONE'

export type ReadPhysicalCardLimitConstraints = {
  currency: string
}

export type ApplyPhysicalCardLimitChangeConstraints = {
  currency: string
  requiresIdempotencyKey: boolean
  requiresExpectedVersion: boolean
}

type CapabilityDecisionTransportFields = {
  availability: CapabilityAvailability
  execution: CapabilityExecution
  backing: CapabilityBacking
  reasonCode: string | null
}

/**
 * The read-only view actions. They parameterize nothing, so `constraints` stays
 * `null` even on an AVAILABLE decision — true of these three actions and only
 * these three. Every other action carries exactly one constraint variant
 * whenever it is not UNAVAILABLE.
 *
 * `MANAGE_PHYSICAL_CARD_MCC` above is also typed `constraints: null`, but that
 * is a different rule and not an exception to this one: every UNAVAILABLE
 * decision has null constraints, and MCC is UNAVAILABLE in every state this
 * slice can produce. What distinguishes the three below is null constraints
 * while AVAILABLE.
 */
export type CardManagementViewAction =
  | 'VIEW_PHYSICAL_CARD_MANAGEMENT'
  | 'READ_PHYSICAL_CARD_ACTIVITY'
  | 'READ_PHYSICAL_CARD_STATEMENTS'

export type CapabilityDecisionResponse = CapabilityDecisionTransportFields &
  (
    | {
        action: 'READ_PHYSICAL_CARD_LIMIT'
        constraints: ReadPhysicalCardLimitConstraints | null
      }
    | {
        action: 'APPLY_PHYSICAL_CARD_LIMIT_CHANGE'
        constraints: ApplyPhysicalCardLimitChangeConstraints | null
      }
    | {
        action: 'MANAGE_PHYSICAL_CARD_MCC'
        constraints: null
      }
    | {
        action: CardManagementViewAction
        constraints: null
      }
  )

export type CardManagementAction = CapabilityDecisionResponse['action']

/**
 * Whether the server says this actor may perform `action` on this card.
 *
 * Keyed by `action`, never by array index — the contract emits the entries in a
 * documented order but explicitly requires consumers not to depend on it.
 *
 * Fails closed on anything that is not an outright AVAILABLE: undefined
 * capabilities (the query is pending, errored, or disabled), a missing entry (an
 * older server that predates the action), and READ_ONLY alike. Callers that OR
 * this with a client flag therefore degrade to the flag rather than to an
 * accidental grant.
 */
export const isCardManagementActionAvailable = (
  capabilities: CapabilityDecisionResponse[] | undefined,
  action: CardManagementAction,
): boolean =>
  capabilities?.find((capability) => capability.action === action)
    ?.availability === 'AVAILABLE'

export interface CardManagementCapabilitiesResponse {
  creditCardId: string
  capabilities: CapabilityDecisionResponse[]
}

export type AvailableSpendCalculation =
  | 'PARTNER_REPORTED'
  | 'PERMANENT_CONTROL_MINUS_BALANCE'

export type CardLimitsProvenance = 'SURROGATE_PARTNER' | 'LIVE_PARTNER'

export interface CardLimitsResponse {
  creditCardId: string
  currency: string
  permanentLimit: number
  availableSpend: number
  currentBalance: number
  version: number
  availableSpendCalculation: AvailableSpendCalculation
  provenance: CardLimitsProvenance
  sourceAsOf: string
}

export interface LimitChangeRequest {
  kind: 'PERMANENT'
  permanentLimit: number
  currency: string
  expectedVersion: number
}

export interface LimitChangeResponse {
  operationId: string
  status: 'SUCCEEDED'
  limits: CardLimitsResponse
}

export interface CardManagementErrorResponse {
  error: string
  errorCode: string
}

export type PhysicalCardManagementStatus =
  | 'PENDING_ISSUANCE'
  | 'ACTIVE'
  | 'FROZEN'
  | 'LOCKED'
  | 'CANCELED'
  | 'FAILED'

export interface CardManagementStatusResponse {
  creditCardId: string
  status: PhysicalCardManagementStatus
  version: number
}

export interface CardManagementStatusChangeRequest {
  expectedVersion: number
}

export interface CardManagementStatusChangeResponse {
  operationId: string
  status: 'PENDING' | 'SUCCEEDED'
  card: CardManagementStatusResponse
}

export type CreatePermanentLimitChangeInput = {
  creditCardId: string
  request: LimitChangeRequest
  idempotencyKey: string
}

export type CreateCardManagementStatusChangeInput = {
  creditCardId: string
  request: CardManagementStatusChangeRequest
  idempotencyKey: string
}
